import type { AuthGateway } from '../../application/AuthGateway'
import { AuthFailure, type AppSession, type LoginCredentials, type PermissionKey } from '../../domain/auth'

export const fixtureSessionStorageKey = 'reca-fixture-session-role'

const allPermissions: PermissionKey[] = [
  'dashboard.read',
  'lead.read',
  'lead.create',
  'lead.update',
  'lead.change_status',
  'lead.assign',
  'lead.archive',
  'lead.convert',
  'lead.delete',
  'quote.read',
  'quote.create',
  'quote.update',
  'quote.send',
  'client.read',
  'client.create',
  'client.update',
  'client.read_documents',
  'client.write_documents',
  'client.change_status',
  'client.notes_read',
  'client.notes_create',
  'client.notes_update',
  'client.notes_delete',
  'client.delete',
  'contract.read',
  'contract.activate',
  'contract.create',
  'contract.update',
  'contract.send',
  'contract.settings_manage',
  'contract.price_override',
  'contract.follow_up',
  'contract.cancel',
  'contract.delete',
  'contract.restore',
  'marker.read',
  'marker.update',
  'renewal_campaign.manage',
  'route.read',
  'route.create',
  'route.update',
  'sector.read',
  'sector.edit',
  'sector.publish',
  'dispatch.read',
  'dispatch.edit',
  'dispatch.publish',
  'mission.read',
  'mission.create',
  'mission.assign',
  'mission.supervise',
  'mission.mark_ready',
  'mission.start',
  'mission.pause',
  'mission.resume',
  'mission.complete',
  'invoice.read',
  'invoice.create',
  'invoice.issue',
  'payment.read',
  'payment.record',
  'payment.read_documents',
  'payment.write_documents',
  'employee.read',
  'employee.manage',
  'employee.assign',
  'equipment.read',
  'equipment.manage',
  'security_log.read',
  'settings.read',
  'settings.manage',
  'users.manage',
  'roles.manage',
  'feedback.create',
  'feedback.manage',
  'email.read',
  'email.manage',
  'sms.read',
  'sms.reply',
  'sms.campaign',
  'sms.templates',
  'sms.consent_proof',
  'sms.settings',
  'sms.log',
  'assigned_mission.read',
  'report.read',
  'report.export_operational',
  'report.export_financial',
  'report.history_all',
  'report.delete',
  'backup.create',
]

export const fixtureSessions = {
  administrator: {
    authUserId: '00000000-0000-4000-a000-000000000001',
    userId: '00000000-0000-4000-b000-000000000001',
    organizationId: '00000000-0000-4000-c000-000000000001',
    organizationName: 'Groupe RECA · Démonstration',
    displayName: 'Jean-François Roy',
    email: 'admin@reca.local',
    status: 'ACTIVE',
    roles: ['ADMINISTRATOR'],
    permissions: allPermissions,
    employeeId: '00000000-0000-4000-d000-000000000001',
    canOperate: true,
  },
  viewer: {
    authUserId: '00000000-0000-4000-a000-000000000002',
    userId: '00000000-0000-4000-b000-000000000002',
    organizationId: '00000000-0000-4000-c000-000000000001',
    organizationName: 'Groupe RECA · Démonstration',
    displayName: 'Camille Gagnon',
    email: 'viewer@reca.local',
    status: 'ACTIVE',
    roles: ['VIEWER'],
    permissions: [
      'dashboard.read',
      'lead.read',
      'quote.read',
      'client.read',
      'client.notes_read',
      'contract.read',
      'route.read',
      'mission.read',
      'invoice.read',
      'feedback.create',
    ],
    canOperate: false,
  },
} as const satisfies Record<string, AppSession>

const fixturePassword = 'DemoReca2026!'

export function createFixtureAuthGateway(): AuthGateway {
  return {
    getSession() {
      const key = window.localStorage.getItem(fixtureSessionStorageKey)
      return Promise.resolve(key === 'administrator' || key === 'viewer' ? fixtureSessions[key] : null)
    },
    signIn({ email, password }: LoginCredentials) {
      const key = email.toLowerCase() === fixtureSessions.viewer.email ? 'viewer' : 'administrator'
      const expectedEmail = fixtureSessions[key].email
      if (email.toLowerCase() !== expectedEmail || password !== fixturePassword) {
        return Promise.reject(new AuthFailure('INVALID_CREDENTIALS', 'Courriel ou mot de passe invalide.'))
      }
      window.localStorage.setItem(fixtureSessionStorageKey, key)
      return Promise.resolve(fixtureSessions[key])
    },
    signOut() {
      window.localStorage.removeItem(fixtureSessionStorageKey)
      return Promise.resolve()
    },
    requestPasswordReset() {
      return Promise.resolve()
    },
    updatePassword() {
      return Promise.resolve()
    },
    replaceTemporaryPassword() {
      const key =
        window.localStorage.getItem(fixtureSessionStorageKey) === 'viewer' ? 'viewer' : 'administrator'
      return Promise.resolve(fixtureSessions[key])
    },
    subscribe() {
      return () => undefined
    },
  }
}
